import {
  Group,
  Image,
  Stack,
  Text,
  Title,
  Button as MantineButton,
} from "@mantine/core"
import { useClipboard } from "@mantine/hooks"
import { IconCheck, IconCopy, IconExternalLink } from "@tabler/icons-react"
import {
  LabelWithTooltip,
  Card,
  Button,
  Switch,
  BoolCell,
  Missing,
  PercentageCell,
} from "@tfg/ui"
import { TFunction } from "i18next"
import { useTranslation } from "react-i18next"

import { RecycledMaterialPercentageCause } from "@tfg/types/endpoints/agec"
import { LibraryResponses } from "@tfg/types/endpoints/library"

import { AGECCountryProperty } from "~/pages/Brand/Products/Details/AGECCountryProperty.tsx"
import { useUpdateAgecShowMutation } from "~/pages/Brand/Products/Details/hooks.ts"
import { useAuth } from "~/store/auth-context.tsx"

import styles from "../../../../assets/css/Product.module.css"
import { MaterialPercentage } from "~/pages/Brand/Regulations/AGEC/MaterialPercentage"
import { useHasAgecComplianceFeatureAccess } from "~/pages/Brand/Regulations/AGEC/useHasAgecComplianceFeatureAccess"

type Props = {
  product: LibraryResponses.ProductDetails
}

const getRecycledMaterialCauseTranslation = (
  t: TFunction<"products", undefined>,
  cause: RecycledMaterialPercentageCause
) => {
  const translationLabels = {
    [RecycledMaterialPercentageCause.BY_ENTERED_COMPONENTS_RECYCLED_PERCENTAGE]:
      t(
        "details.agec.recyclability.tooltipByCause.byEnteredComponentsRecycledPercentage"
      ),
    [RecycledMaterialPercentageCause.BY_INGESTED_MATERIAL_COMPOSITION_RECYCLED_PERCENTAGE]:
      t(
        "details.agec.recyclability.tooltipByCause.byIngestedMaterialCompositionRecycledPercentage"
      ),
    [RecycledMaterialPercentageCause.BY_INGESTED_PRODUCT_RECYCLED_PERCENTAGE]:
      t(
        "details.agec.recyclability.tooltipByCause.byIngestedProductRecycledPercentage"
      ),
    [RecycledMaterialPercentageCause.BY_MIXED_COMPONENTS_RECYCLED_PERCENTAGE]:
      t(
        "details.agec.recyclability.tooltipByCause.byMixedComponentsRecycledPercentage"
      ),
    [RecycledMaterialPercentageCause.NOT_CALCULATED]: t(
      "details.agec.recyclability.tooltipByCause.notCalculated"
    ),
    [RecycledMaterialPercentageCause.ERROR]: t(
      "lists.cells.agec.materialPercentage.metadataComputationErrorTooltip"
    ),
  }
  return translationLabels[cause]
}

export const AGECDetails = ({ product }: Props) => {
  const { t } = useTranslation("products")
  const { copy, copied } = useClipboard()

  const { user } = useAuth()

  const { mutate } = useUpdateAgecShowMutation(product.id)

  const hasAgecComplianceAccess = useHasAgecComplianceFeatureAccess()

  return (
    <Group align="flex-start">
      <Card w="fit-content">
        <Stack>
          <Title order={4}>{t("details.agec.title.traceability")}</Title>
          {product.agecActivityCountries.map((activity) => (
            <AGECCountryProperty
              key={activity.activityName}
              activityName={activity.activityName}
              countryInfo={activity.country}
            />
          ))}
          <Title order={4}>{t("details.agec.title.other")}</Title>
          <Group gap="xs">
            <Text size="sm">{t("details.agec.microplastics")}:</Text>
            <BoolCell
              size="sm"
              value={
                product.releaseMicroplastics !== "missing-main-component"
                  ? product.releaseMicroplastics
                  : undefined
              }
            />
          </Group>

          <Group gap="xs">
            <LabelWithTooltip
              label={t("details.agec.recyclability.title")}
              labelClassName={styles.customFieldLabel}
              hoverHelperText={getRecycledMaterialCauseTranslation(
                t,
                product.recycledMaterial.cause
              )}
            />
            <MaterialPercentage
              percentage={product.recycledMaterial}
              type="recycled"
            />
          </Group>
          {hasAgecComplianceAccess ? (
            <Group gap="xs">
              <Text size="sm">{t("details.agec.metalloplastic.title")}:</Text>
              <MaterialPercentage
                percentage={product.metalloplasticPercentage}
                type="metalloplastic"
              />
            </Group>
          ) : null}
          <Title order={4}>{t("details.agec.primaryPackaging.title")}</Title>
          <Group gap="xs">
            <Text size="sm">
              {t("details.agec.primaryPackaging.withPrimaryPackaging")}:
            </Text>
            <BoolCell size="sm" value={product.withPrimaryPackaging} />
          </Group>
          {product.withPrimaryPackaging && (
            <>
              <Group gap="xs">
                <Text size="sm">
                  {t("details.agec.primaryPackaging.primaryPackagingRecycled")}:
                </Text>
                <BoolCell
                  size="sm"
                  value={product.primaryPackagingRecyclable}
                />
              </Group>
              <Group gap="xs">
                <Text size="sm">
                  {t(
                    "details.agec.primaryPackaging.primaryPackagingRecycledPercentage"
                  )}
                  :
                </Text>
                {product.primaryPackagingRecycledPercentage === null ? (
                  <Missing />
                ) : (
                  <PercentageCell
                    isCalculated
                    percentage={product.primaryPackagingRecycledPercentage}
                    showOutOfRangeWarning
                  />
                )}
              </Group>
              <Group gap="xs">
                <Text size="sm">
                  {t(
                    "details.agec.primaryPackaging.primaryPackagingHazardousSubstances"
                  )}
                  :
                </Text>
                <BoolCell
                  size="sm"
                  value={product.primaryPackagingHazardousSubstances}
                />
              </Group>
            </>
          )}
        </Stack>
      </Card>

      {user?.organization?.settings?.agecProductPagePlan === "ADVANCED" ? (
        <Card w="fit-content">
          <Stack>
            <Switch
              label={
                product.agecShow
                  ? t("details.switch.visible")
                  : t("details.switch.hidden")
              }
              checked={product.agecShow}
              onChange={(val) => {
                mutate({
                  agecShow: val,
                })
              }}
            />
            <Title order={4}>{t("details.agec.title.qrCode")}</Title>
            <Group justify="space-between" align="start">
              <Image
                src={product.agecQrCodeUrl}
                w={170}
                h={170}
                alt="QR Code"
              />
              <Stack>
                <MantineButton
                  variant="secondary"
                  rightSection={<IconExternalLink />}
                  component="a"
                  target="_blank"
                  href={product.agecPopinUrl ?? undefined}
                >
                  {t("details.agec.qrCode.viewProductSheet")}
                </MantineButton>
                <Button
                  variant="secondary"
                  icon={copied ? <IconCheck /> : <IconCopy />}
                  onClick={() => copy(product.agecPopinUrl)}
                >
                  {copied
                    ? t("details.agec.qrCode.copied")
                    : t("details.agec.qrCode.copyLink")}
                </Button>
              </Stack>
            </Group>
          </Stack>
        </Card>
      ) : undefined}
    </Group>
  )
}
